import { useEffect, useRef } from 'react'
import { useActionState, useOptimistic } from 'react'
import * as m from 'framer-motion/m'
import { toast } from 'sonner'
import { Container } from './Container'

const API_BASE = (import.meta.env.VITE_API_URL as string) || ''

function validateEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

type ContactState = { status: 'idle' | 'success' | 'error'; message?: string }

async function contactAction(_prevState: ContactState, formData: FormData): Promise<ContactState> {
  const email = (formData.get('email') as string)?.trim() || ''
  if (!validateEmail(email)) {
    return { status: 'error', message: 'Please enter a valid email address.' }
  }
  try {
    const res = await fetch(`${API_BASE}/api/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: (formData.get('name') as string)?.trim() || '',
        email,
        message: (formData.get('message') as string)?.trim() || '',
      }),
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error((data as { error?: string }).error || 'Something went wrong')
    }
    return { status: 'success' }
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Something went wrong. Please try again or email rose@impact-global.com.'
    return { status: 'error', message }
  }
}

const initialState: ContactState = { status: 'idle' }

export function ContactForm() {
  const [state, formAction, isPending] = useActionState(contactAction, initialState)
  const [optimisticState, addOptimistic] = useOptimistic(
    state,
    (_current, optimistic: ContactState) => optimistic
  )
  const lastToastedRef = useRef<string | null>(null)

  const displayStatus = optimisticState.status
  const showSuccess = displayStatus === 'success'
  const showError = state.status === 'error'
  const isEmailValidationError = state.message?.includes('valid email')

  useEffect(() => {
    if (showError && state.message && lastToastedRef.current !== state.message) {
      toast.error(state.message)
      lastToastedRef.current = state.message
    }
    if (state.status !== 'error') lastToastedRef.current = null
  }, [showError, state.status, state.message])

  const handleSubmit = (formData: FormData) => {
    addOptimistic({ status: 'success' })
    return formAction(formData)
  }

  return (
    <section className="bg-[#1e232b] border-t border-white/10" id="contact">
      <Container>
        <m.div
          className="py-12 sm:py-16 md:py-20 flex flex-col items-center text-center"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="uppercase text-[#cab6a1] text-[24px] sm:text-[28px] md:text-[38px] font-serif mb-4">
            Contact Us
          </h2>
          {!showSuccess && (
            <p className="text-white/80 text-sm font-sans mb-6 sm:mb-8 max-w-md">
              Interested in partnering with us? We&apos;d love to hear from you.
            </p>
          )}

          {showSuccess ? (
            <p className="text-[#cab6a1] text-[20px] sm:text-[22px] font-serif">
              Thank you, we will be in touch.
            </p>
          ) : (
            <form
              action={handleSubmit}
              className="max-w-md w-full space-y-4 mx-auto px-0 text-left"
            >
              {showError && state.message && (
                <div
                  className="flex items-start gap-3 p-4 rounded-lg bg-red-500/10 border border-red-400/30 text-left"
                  role="alert"
                >
                  <svg className="w-5 h-5 text-red-400 shrink-0 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                  <p className="text-red-200 text-sm font-sans leading-snug">
                    {state.message}
                  </p>
                </div>
              )}
              <div>
                <label htmlFor="contact-name" className="sr-only">
                  Name
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  placeholder="Name"
                  className="w-full px-4 py-3 bg-[#1e232b] border border-white/20 text-white placeholder-white/50 rounded focus:outline-none focus:border-[#cab6a1] focus-visible:ring-2 focus-visible:ring-[#cab6a1] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1e232b] font-sans text-sm"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="sr-only">
                  Email
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  placeholder="Email"
                  className={`w-full px-4 py-3 bg-[#1e232b] border text-white placeholder-white/50 rounded focus:outline-none focus:border-[#cab6a1] focus-visible:ring-2 focus-visible:ring-[#cab6a1] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1e232b] font-sans text-sm ${isEmailValidationError ? 'border-red-400' : 'border-white/20'}`}
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="sr-only">
                  Message
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  placeholder="Message"
                  rows={4}
                  className="w-full px-4 py-3 bg-[#1e232b] border border-white/20 text-white placeholder-white/50 rounded focus:outline-none focus:border-[#cab6a1] focus-visible:ring-2 focus-visible:ring-[#cab6a1] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1e232b] resize-y font-sans text-sm min-h-[120px]"
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isPending}
                  aria-label={isPending ? 'Sending…' : 'Send message'}
                  title={isPending ? 'Sending…' : 'Send'}
                  className="w-12 h-12 flex items-center justify-center bg-[#cab6a1] text-[#1e232b] hover:opacity-90 disabled:opacity-60 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[#cab6a1] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1e232b]"
                >
                  {isPending ? (
                    <svg className="w-5 h-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden>
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden>
                      <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z" />
                      <path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z" />
                    </svg>
                  )}
                </button>
              </div>
            </form>
          )}
        </m.div>
      </Container>
    </section>
  )
}
